import useSidesheetById from 'app/hooks/useSidesheetById';
import SideSheetContent from 'app/shared-components/sidesheets/SideSheetContent';
import SideSheetHeader from 'app/shared-components/sidesheets/SideSheetHeader';
import NewCardForm from './NewCardForm';
import { CardGroup, Role } from 'API';
import { selectCurrentPaidolUser, selectUserCompanies } from 'app/store/userCompaniesSlice';
import { selectAllSpendRules } from 'app/pages/store/spendRulesSlice';
import { SidesheetRoutes, useSidesheet } from 'app/shared-components/sidesheets/SidesheetProvider';
import { getAllGroups, selectAllGroups } from 'app/pages/store/groupsSlice';
import { useAppDispatch, useAppSelector } from 'app/store';
import { useCallback, useEffect, useMemo } from 'react';
import { NewCardSheetProps, useIssueOrUpdateCard } from 'app/hooks/useIssueOrUpdateCard';
import {
  resetOpenCardDetail,
  selectCardsDataSlice,
  setOpenCardDetail,
} from 'app/pages/store/CardDataSlice/cardDataSlice';
import { getCard, getCardList } from 'app/pages/store/CardDataSlice/cardsThunks';

const sortByName = <T extends { name: string }>(items: T[]): T[] => {
  return [...items].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
};

function NewCardSheet(): JSX.Element {
  const dispatch = useAppDispatch();
  const sidesheet = useSidesheetById(SidesheetRoutes.NewCard);
  const params: NewCardSheetProps = sidesheet.params;

  const { formType } = params;
  const { closeSidesheet } = useSidesheet();

  const { userRole, selectedPaidolId } = useAppSelector(selectUserCompanies);
  // SEE-1352: the groups a Group Admin manages are per-membership, so this has
  // to be the membership for the selected company — `paidolUsers[0]` handed a
  // switcher user the other company's groups.
  const currentPaidolUser = useAppSelector(selectCurrentPaidolUser);
  const merchantSpendRules = useAppSelector(selectAllSpendRules);
  const cardState = useAppSelector(selectCardsDataSlice);
  const groups = useAppSelector(selectAllGroups);
  const allGroups = sortByName(groups);

  const managedGroups = useMemo(() => {
    const groups =
      currentPaidolUser?.managedGroups?.items
        ?.map((item) => item?.cardGroup)
        ?.filter((group): group is CardGroup => Boolean(group)) ?? [];

    return sortByName(groups);
  }, [currentPaidolUser]);

  useEffect(() => {
    dispatch(getAllGroups(selectedPaidolId));
  }, [dispatch, selectedPaidolId]);

  useEffect(() => {
    if (sidesheet.isOpen && params.selectedPaymentCard) {
      dispatch(setOpenCardDetail(params.selectedPaymentCard));
    }
  }, [sidesheet.isOpen, params.selectedPaymentCard, dispatch]);

  const handleClose = useCallback(() => {
    dispatch(resetOpenCardDetail());

    // Only refresh the full card list for new cards, not for updates
    // Updates are handled by getCard.fulfilled which updates the specific card
    if (formType !== 'UPDATE_CARD') {
      dispatch(getCardList({ filter: { paidolId: { eq: selectedPaidolId } } }));
    }

    closeSidesheet(SidesheetRoutes.NewCard);
  }, [closeSidesheet, dispatch, selectedPaidolId, formType]);

  const { onSaveCard, loading } = useIssueOrUpdateCard({
    ...params,
    selectedPaymentCard: cardState.openCardDetail,
    handleClose,
    // Add this to ensure the callback is properly handled
    onUpdateCard: params.onUpdateCard,
  });

  // Fetch fresh card data when sidesheet opens for edit/order forms
  useEffect(() => {
    const isEditOrOrder = formType === 'UPDATE_CARD' || formType === 'ORDER_PHYSICAL';
    const paymentCardId = params.selectedPaymentCard?.paymentCardId;

    if (isEditOrOrder && paymentCardId && selectedPaidolId && sidesheet.isOpen) {
      dispatch(
        getCard({
          filter: {
            and: [{ or: [{ paymentCardId: { eq: paymentCardId } }] }],
            paidolId: { eq: selectedPaidolId },
          },
        })
      );
    }
  }, [dispatch, selectedPaidolId, params.selectedPaymentCard?.paymentCardId, formType, sidesheet.isOpen]);

  return (
    <>
      <SideSheetHeader
        title={
          formType === 'ISSUE_CARD'
            ? 'New card'
            : formType === 'UPDATE_CARD'
            ? 'Edit card'
            : 'Order physical card'
        }
        onClose={handleClose}
      />
      <SideSheetContent>
        <NewCardForm
          formType={formType}
          paymentCard={cardState.openCardDetail}
          loading={loading}
          groups={userRole === Role.GROUP_ADMINISTRATOR ? managedGroups : allGroups}
          rules={merchantSpendRules}
          onSubmit={onSaveCard}
          onCancel={handleClose}
        />
      </SideSheetContent>
    </>
  );
}

export default NewCardSheet;
